<template>
  <modal
    name="offstreet-permit-management"
    :classes="'customModal'"
    :scrollable="true"
    :reset="true"
    :adaptive="false"
    :width="1200"
    height="auto"
    @before-open="initializeModal"
  >
    <div class="modalWrapper">
      <div class="content__box">
        <div class="content__box__header d-flex justify-content-between align-items-center">
          <div>
            <p class="text-white txt--ss opacity--50 mb-1">Offstreet Management</p>
            <p class="txt--xs txt--off-white mb-0">
              {{ permit.permit_name || 'Permit' }}
              <span v-if="permit.permit_id">({{ permit.permit_id }})</span>
            </p>
          </div>
          <div class="d-flex">
            <button
              type="button"
              class="button button--outline button--sm mr-2"
              @click="openPropertyMapping"
              v-if="canWrite"
            >
              Property Mapping
            </button>
            <button type="button" class="button button--outline button--sm" @click="closeModal">Close</button>
          </div>
        </div>
        <div class="content__divider"></div>

        <div class="content__box__body">
          <div class="offstreet-grid">
            <div class="offstreet-panel">
              <h3 class="txt--ss txt--off-white mb-3">Permit Settings</h3>
              <div class="offstreet-context mb-3">
                <p class="txt--xs txt--off-white mb-1">
                  Effective location: <strong>{{ permitOffstreetSummary.effectiveLocation || "Not mapped" }}</strong>
                </p>
                <p class="txt--xs mb-1">
                  <span class="offstreet-source-chip" :class="'source-' + permitOffstreetSummary.source">
                    Source: {{ permitOffstreetSummary.source }}
                  </span>
                </p>
                <p class="txt--xs txt--off-white opacity--50 mb-0" v-if="permitOffstreetSummary.source === 'property'">
                  This permit currently uses property fallback. Clearing permit-level location will continue to use the property location.
                </p>
              </div>
              <div class="mb-3">
                <app-toggle
                  :checked="permitConfig.offstreet_syncable"
                  :label="'Offstreet sync enabled'"
                  @update:checked="permitConfig.offstreet_syncable = $event"
                ></app-toggle>
              </div>
              <div class="form-group mb-3">
                <label class="txt--xs txt--off-white mb-2">Offstreet location id</label>
                <input
                  type="text"
                  class="textInputField"
                  placeholder="Enter Offstreet location id"
                  v-model.trim="permitConfig.offstreet_location_id"
                />
              </div>
              <ButtonWithSpinner
                class="button button--primary button--sm"
                :loading="saveConfigButton.loading"
                :success="saveConfigButton.success"
                :disabled="saveConfigButton.disabled || !canWrite"
                @click="savePermitConfig"
              >
                Save Settings
              </ButtonWithSpinner>
              <p v-if="recreateQueuedCount !== null" class="txt--xs txt--off-white mt-2 mb-0">
                Recreate queued: {{ recreateQueuedCount }}
              </p>
            </div>

            <div class="offstreet-panel">
              <h3 class="txt--ss txt--off-white mb-3">Sync Actions</h3>
              <div class="mb-3">
                <app-toggle
                  :checked="syncAsync"
                  :label="'Run async (recommended)'"
                  @update:checked="syncAsync = $event"
                ></app-toggle>
              </div>
              <div class="d-flex flex-wrap align-items-center">
                <ButtonWithSpinner
                  class="button button--primary button--sm mr-2 mb-2"
                  :loading="syncButtons.smart"
                  :success="false"
                  :disabled="!canWrite"
                  @click="triggerSync('smart')"
                >
                  Sync Now (Smart)
                </ButtonWithSpinner>
                <ButtonWithSpinner
                  class="button button--outline button--sm mr-2 mb-2"
                  :loading="syncButtons.force"
                  :success="false"
                  :disabled="!canWrite"
                  @click="confirmAndForceSync"
                >
                  Sync Now (Force)
                </ButtonWithSpinner>
              </div>
              <div class="d-flex flex-wrap align-items-center mt-2">
                <ButtonWithSpinner
                  class="button button--outline button--sm mr-2 mb-2"
                  :loading="retryButtons.failed"
                  :success="false"
                  :disabled="!canWrite"
                  @click="retryFailed"
                >
                  Retry Failed Recreates
                </ButtonWithSpinner>
                <ButtonWithSpinner
                  class="button button--outline button--sm mb-2"
                  :loading="retryButtons.voids"
                  :success="false"
                  :disabled="!canWrite"
                  @click="confirmAndRetryVoids"
                >
                  Retry Pending Voids
                </ButtonWithSpinner>
              </div>
            </div>
          </div>

          <div v-if="hints.recommended_actions && hints.recommended_actions.length" class="offstreet-hints mt-3">
            <p class="txt--xs txt--off-white mb-1"><strong>Operator hints</strong></p>
            <p class="txt--xs txt--off-white mb-1" v-if="hints.review_subscription_sync_status">
              Review subscription sync status is recommended.
            </p>
            <div class="d-flex flex-wrap align-items-center">
              <button
                type="button"
                class="offstreet-hint-pill"
                v-for="(action, index) in hints.recommended_actions"
                :key="index"
                @click="handleHintAction(action)"
              >
                {{ action }}
              </button>
            </div>
          </div>

          <div v-if="failedDetails.length" class="offstreet-hints mt-3">
            <details>
              <summary class="txt--xs txt--off-white cursor-pointer"><strong>Failure details ({{ failedDetails.length }})</strong></summary>
              <div class="table-responsive mt-2">
                <table class="table table__audit table-borderless mb-0">
                  <thead>
                    <tr>
                      <th>Subscription</th>
                      <th>Permit</th>
                      <th>Reason</th>
                      <th>Message</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, idx) in failedDetails" :key="idx">
                      <td>{{ item.subscription_id }}</td>
                      <td>{{ item.permit_id }}</td>
                      <td>{{ item.reason }}</td>
                      <td>{{ item.message }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </details>
          </div>

          <div class="mt-4">
            <div class="d-flex flex-wrap align-items-center justify-content-between mb-2">
              <h3 class="txt--ss txt--off-white mb-0">Offstreet Status</h3>
              <div class="d-flex flex-wrap align-items-center">
                <button
                  v-for="item in statusFilters"
                  :key="item"
                  class="offstreet-filter-chip"
                  :class="{ active: statusFilter === item }"
                  @click="setStatusFilter(item)"
                >
                  {{ item }}
                </button>
              </div>
            </div>

            <div v-if="statusSummary.total > 0" class="offstreet-summary-row mb-3">
              <span class="offstreet-summary-pill">
                Total: {{ statusSummary.total }}
              </span>
              <span class="offstreet-summary-pill summary-good">
                Synced: {{ statusSummary.synced }}
              </span>
              <span class="offstreet-summary-pill summary-danger">
                Failed: {{ statusSummary.failed }}
              </span>
              <span class="offstreet-summary-pill summary-subdued">
                Unsynced: {{ statusSummary.unsynced }}
              </span>
              <span class="offstreet-summary-pill summary-warn">
                Voided: {{ statusSummary.voided }}
              </span>
              <span class="offstreet-summary-pill summary-neutral">
                Needs recreate: {{ statusSummary.needs_recreate }}
              </span>
            </div>

            <div class="table-responsive">
              <table class="table table__audit table-borderless">
                <thead>
                  <tr>
                    <th>Subscription ID</th>
                    <th>Parker</th>
                    <th>Local status</th>
                    <th>Sync status</th>
                    <th>Cutover</th>
                    <th>Needs recreate</th>
                    <th>Permit version</th>
                    <th>Last updated</th>
                    <th>Last failed</th>
                    <th>Recreate error</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody v-if="statusRows.length">
                  <tr v-for="row in statusRows" :key="row.subscription_id">
                    <td>
                      <span class="txt--primary cursor-pointer" @click="openPermitCustomer(row)">{{ row.subscription_id }}</span>
                    </td>
                    <td>
                      <p class="mb-0">{{ row.parker_name || 'N/A' }}</p>
                      <p class="mb-0 txt--xs opacity--50">{{ row.parker_email || 'N/A' }}</p>
                    </td>
                    <td>{{ row.status || 'N/A' }}</td>
                    <td>
                      <span class="offstreet-status-chip" :class="syncStatusClass(row.sync_status)">
                        {{ row.sync_status || 'unknown' }}
                      </span>
                    </td>
                    <td>
                      <span class="offstreet-status-chip offstreet-cutover-chip" :title="cutoverTooltip(row.cutover_status)">
                        {{ row.cutover_status || 'none' }}
                      </span>
                    </td>
                    <td>{{ row.offstreet_needs_recreate ? 'Yes' : 'No' }}</td>
                    <td>{{ row.offstreet_permit_version }}</td>
                    <td>{{ formatDate(row.offstreet_last_updated_at || row.offstreet_created_at) }}</td>
                    <td>{{ formatDate(row.offstreet_last_failed_at || row.offstreet_recreate_failed_at) }}</td>
                    <td class="offstreet-error-cell">{{ row.offstreet_recreate_last_error || '-' }}</td>
                    <td>
                      <button
                        class="txt--primary offstreet-inline-action"
                        @click.stop="confirmAndRecreate(row)"
                        :disabled="!canWrite || rowRecreateLoadingId === row.subscription_id"
                      >
                        {{ rowRecreateLoadingId === row.subscription_id ? 'Running...' : 'Recreate' }}
                      </button>
                    </td>
                  </tr>
                </tbody>
                <tbody v-else>
                  <tr>
                    <td colspan="11" class="text-center">
                      <div v-if="statusLoading" class="d-flex justify-content-center">
                        <div class="lds-ring">
                          <div></div>
                          <div></div>
                          <div></div>
                          <div></div>
                        </div>
                      </div>
                      <span v-else>No Offstreet status records found</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="d-flex justify-content-between align-items-center" v-if="statusRows.length">
              <p class="txt--xs txt--off-white mb-0">
                Page {{ pagination.page }} of {{ pagination.last_page || 1 }}
                | Total subscriptions: {{ pagination.total || statusRows.length }}
                | Per page: {{ pagination.per_page }}
              </p>
              <div>
                <button class="button button--outline button--sm mr-2" :disabled="pagination.page <= 1 || statusLoading" @click="changePage(-1)">
                  Previous
                </button>
                <button class="button button--outline button--sm" :disabled="!pagination.hasMore || statusLoading" @click="changePage(1)">
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </modal>
</template>

<script>
import Toggle from "@/components/toggle";
import ButtonWithSpinner from "@/components/Button";
import {
  updateOffstreetPermitConfig,
  triggerOffstreetSync,
  fetchOffstreetSubscriptionSyncStatus,
  recreateOffstreetSubscription,
  retryFailedOffstreetRecreates,
  retryPendingOffstreetVoids,
} from "@/services/offstreetOperator.service";

export default {
  name: "OffstreetPermitManagement",
  components: {
    AppToggle: Toggle,
    ButtonWithSpinner,
  },
  data() {
    return {
      permit: {},
      permitConfig: {
        offstreet_syncable: false,
        offstreet_location_id: "",
      },
      saveConfigButton: {
        loading: false,
        success: false,
        disabled: false,
      },
      syncAsync: true,
      syncButtons: {
        smart: false,
        force: false,
      },
      retryButtons: {
        failed: false,
        voids: false,
      },
      statusFilters: ["all", "synced", "unsynced", "failed", "voided"],
      statusFilter: "all",
      statusRows: [],
      statusLoading: false,
      statusSummary: {
        total: 0,
        synced: 0,
        failed: 0,
        voided: 0,
        unsynced: 0,
        needs_recreate: 0,
      },
      pagination: {
        page: 1,
        per_page: 25,
        total: 0,
        last_page: 1,
        hasMore: false,
      },
      rowRecreateLoadingId: "",
      recreateQueuedCount: null,
      hints: {
        review_subscription_sync_status: false,
        recommended_actions: [],
      },
      failedDetails: [],
      userRole: "",
      accessList: {},
    };
  },
  computed: {
    canWrite() {
      return this.userRole === "commercial" || (this.userRole === "operator" && this.accessList && this.accessList.permit_access_write === true);
    },
    permitOffstreetSummary() {
      const offstreet = this.permit && this.permit.offstreet ? this.permit.offstreet : {};
      const source = String(
        this.permit.offstreet_location_source || offstreet.location_source || "none"
      ).toLowerCase();
      const effectiveLocation =
        this.permit.offstreet_effective_location_id ||
        offstreet.effective_location_id ||
        this.permit.offstreet_location_id ||
        offstreet.location_id ||
        null;

      return {
        source: ["permit", "property", "none"].includes(source) ? source : "none",
        effectiveLocation,
      };
    },
  },
  created() {
    this.userRole = localStorage.getItem("role") || "";
    try {
      this.accessList = JSON.parse(localStorage.getItem("commercial_access_list")) || {};
    } catch (e) {
      this.accessList = {};
    }
  },
  methods: {
    initializeModal(e) {
      const permit = e && e.params && e.params.permit ? e.params.permit : {};
      this.permit = JSON.parse(JSON.stringify(permit || {}));
      const nestedOffstreet = this.permit && this.permit.offstreet ? this.permit.offstreet : {};
      const syncableValue = this.permit.offstreet_syncable !== undefined ? this.permit.offstreet_syncable : nestedOffstreet.syncable;
      this.permitConfig.offstreet_syncable = !!(syncableValue === true || syncableValue === 1 || syncableValue === "1");
      this.permitConfig.offstreet_location_id = this.permit.offstreet_location_id || nestedOffstreet.location_id || "";
      this.statusFilter = "all";
      this.pagination.page = 1;
      this.pagination.hasMore = false;
      this.recreateQueuedCount = null;
      this.hints = { review_subscription_sync_status: false, recommended_actions: [] };
      this.failedDetails = [];
      this.fetchStatus();
    },
    closeModal() {
      this.$modal.hide("offstreet-permit-management");
    },
    openPropertyMapping() {
      this.$modal.show("offstreet-property-mapping", {
        selected_property_id: this.permit.property_id,
      });
    },
    async openPermitCustomer(row) {
      if (!row || !row.subscription_id || !this.permit.property_id) {
        return;
      }

      const modalParams = {
        property_id: this.permit.property_id,
        property_timezone:
          this.permit.property_timezone ||
          this.permit.time_zone ||
          (this.permit.location && this.permit.location.time_zone) ||
          this.$moment.tz.guess(),
        property_status:
          this.permit.space_status !== undefined
            ? this.permit.space_status
            : (this.permit.status !== undefined ? this.permit.status : 4),
        direct_subscription_id: row.subscription_id,
        direct_permit_id: this.permit.permit_id,
      };

      try {
        const response = await this.axios.post(
          window.hostname + "/api/auth/permit/subscription-show/" + encodeURIComponent(row.subscription_id), {
            commercial: 1,
          }
        );

        if (response && response.data && response.data.status) {
          const responseData = response.data.data || {};
          const preloadedSubscription =
            responseData.subscription ||
            responseData.subscription_details ||
            responseData.subscription_detail ||
            responseData.data ||
            responseData;

          if (preloadedSubscription && typeof preloadedSubscription === "object") {
            modalParams.preloaded_subscription = preloadedSubscription;
          }
        }
      } catch (error) {
        // PermitCustomer has its own direct-load fallback.
      }

      this.$modal.show("permit-customer", modalParams);
    },
    normalizeHints(rawHints) {
      const hints = rawHints || {};
      return {
        review_subscription_sync_status: hints.review_subscription_sync_status === true,
        recommended_actions: Array.isArray(hints.recommended_actions) ? hints.recommended_actions : [],
      };
    },
    extractApiData(response) {
      if (!response || !response.data) {
        return {};
      }
      return response.data.data || response.data || {};
    },
    showError(err) {
      let message = "Something went wrong. Please try again.";
      if (err && err.response && err.response.data && err.response.data.message) {
        message = err.response.data.message;
      }

      if (err && err.response && err.response.status === 422 && /offstreet|config/i.test(message)) {
        message = "Offstreet credentials are managed by Pied admins. Contact support.";
      }

      this.$toasted.show(message, {
        icon: "times-circle",
        className: "error-class",
      });
    },
    async savePermitConfig() {
      if (!this.permit.permit_id) {
        this.$toasted.show("Permit id is missing.", {
          icon: "times-circle",
          className: "error-class",
        });
        return;
      }

      this.saveConfigButton.loading = true;
      this.saveConfigButton.disabled = true;

      try {
        const response = await updateOffstreetPermitConfig({
          permit_id: this.permit.permit_id,
          offstreet_syncable: this.permitConfig.offstreet_syncable,
          offstreet_location_id: this.permitConfig.offstreet_location_id || null,
        });
        const data = this.extractApiData(response);
        this.recreateQueuedCount = data.recreate_queued_count !== undefined ? data.recreate_queued_count : null;
        this.hints = this.normalizeHints(data.operator_hints);
        this.$toasted.show((response.data && response.data.message) || "Offstreet permit settings saved.", {
          icon: "check-circle",
          className: "success-class",
        });
      } catch (err) {
        this.showError(err);
      } finally {
        this.saveConfigButton.loading = false;
        this.saveConfigButton.disabled = false;
      }
    },
    async triggerSync(mode) {
      if (!this.permit.permit_id) {
        return;
      }

      this.syncButtons[mode] = true;

      try {
        const response = await triggerOffstreetSync({
          permit_id: this.permit.permit_id,
          mode,
          async: this.syncAsync,
        });
        const data = this.extractApiData(response);
        if (this.syncAsync) {
          this.$toasted.show((response.data && response.data.message) || "Offstreet sync queued.", {
            icon: "check-circle",
            className: "success-class",
          });
        } else {
          this.$swal({
            title: "Sync complete",
            text: "Total: " + (data.total || 0) + " | Synced: " + (data.synced || 0) + " | Failed: " + (data.failed || 0),
          });
        }
        this.fetchStatus();
      } catch (err) {
        this.showError(err);
      } finally {
        this.syncButtons[mode] = false;
      }
    },
    confirmAndForceSync() {
      this.$swal({
        title: "Force sync",
        text: "Are you sure you want to run force sync?",
        showCancelButton: true,
      }).then((result) => {
        if (result.value) {
          this.triggerSync("force");
        }
      });
    },
    async fetchStatus() {
      if (!this.permit.permit_id) {
        return;
      }

      this.statusLoading = true;

      try {
        const response = await fetchOffstreetSubscriptionSyncStatus({
          permit_id: this.permit.permit_id,
          filter: this.statusFilter,
          page: this.pagination.page,
          per_page: this.pagination.per_page,
        });
        const data = this.extractApiData(response);
        const records =
          data.subscriptions ||
          data.records ||
          data.subscription_records ||
          data.subscription_sync_status ||
          data.list ||
          [];
        this.statusRows = Array.isArray(records)
          ? records.map((row) => {
              return {
                ...row,
                offstreet_created_at: this.normalizeDateValue(row.offstreet_created_at),
                offstreet_last_updated_at: this.normalizeDateValue(row.offstreet_last_updated_at),
                offstreet_voided_at: this.normalizeDateValue(row.offstreet_voided_at),
                offstreet_last_failed_at: this.normalizeDateValue(row.offstreet_last_failed_at),
                offstreet_recreate_requested_at: this.normalizeDateValue(row.offstreet_recreate_requested_at),
                offstreet_recreate_attempted_at: this.normalizeDateValue(row.offstreet_recreate_attempted_at),
                offstreet_recreate_failed_at: this.normalizeDateValue(row.offstreet_recreate_failed_at),
              };
            })
          : [];

        const summary = data.summary || {};
        this.statusSummary = {
          total: Number(summary.total) || 0,
          synced: Number(summary.synced) || 0,
          failed: Number(summary.failed) || 0,
          voided: Number(summary.voided) || 0,
          unsynced: Number(summary.unsynced) || 0,
          needs_recreate: Number(summary.needs_recreate) || 0,
        };

        const pagination = data.pagination || {};
        this.pagination.page = Number(pagination.page) || this.pagination.page;
        this.pagination.per_page = Number(pagination.per_page) || this.pagination.per_page;
        this.pagination.total = Number(pagination.total) || 0;
        this.pagination.last_page = Number(pagination.last_page) || 1;

        if (pagination.load_more !== undefined) {
          this.pagination.hasMore = pagination.load_more === 1 || pagination.load_more === true;
        } else if (pagination.total && pagination.to) {
          this.pagination.hasMore = Number(pagination.to) < Number(pagination.total);
        } else if (this.pagination.last_page) {
          this.pagination.hasMore = this.pagination.page < this.pagination.last_page;
        } else {
          this.pagination.hasMore = this.statusRows.length === this.pagination.per_page;
        }
      } catch (err) {
        this.statusRows = [];
        this.statusSummary = {
          total: 0,
          synced: 0,
          failed: 0,
          voided: 0,
          unsynced: 0,
          needs_recreate: 0,
        };
        this.pagination.total = 0;
        this.pagination.last_page = 1;
        this.pagination.hasMore = false;
        this.showError(err);
      } finally {
        this.statusLoading = false;
      }
    },
    setStatusFilter(filterValue) {
      this.statusFilter = filterValue;
      this.pagination.page = 1;
      this.fetchStatus();
    },
    changePage(direction) {
      const nextPage = this.pagination.page + direction;
      if (nextPage < 1) {
        return;
      }
      this.pagination.page = nextPage;
      this.fetchStatus();
    },
    formatDate(dateText) {
      if (!dateText) {
        return "-";
      }
      return this.$moment(dateText).format("YYYY-MM-DD HH:mm");
    },
    normalizeDateValue(value) {
      if (!value) {
        return null;
      }

      if (typeof value === "string" || typeof value === "number") {
        return value;
      }

      if (value.$date !== undefined) {
        const dateValue = value.$date;
        if (typeof dateValue === "string" || typeof dateValue === "number") {
          return dateValue;
        }
        if (dateValue && dateValue.$numberLong !== undefined) {
          return Number(dateValue.$numberLong);
        }
      }

      if (value.$numberLong !== undefined) {
        return Number(value.$numberLong);
      }

      return value;
    },
    syncStatusClass(status) {
      const value = String(status || "").toLowerCase();
      if (value === "synced") {
        return "status-success";
      }
      if (value === "failed") {
        return "status-danger";
      }
      if (value === "voided") {
        return "status-subdued";
      }
      return "status-neutral";
    },
    cutoverTooltip(status) {
      const value = String(status || "none");
      const descriptions = {
        none: "No cutover flow has started.",
        pending: "Cutover is queued or in progress.",
        create_succeeded_void_pending: "New create succeeded and old permit void is pending.",
        completed: "Create-then-void cutover completed.",
        failed: "Cutover failed and needs remediation.",
      };
      return descriptions[value] || "Cutover status";
    },
    handleHintAction(actionText) {
      const normalized = String(actionText || "").toLowerCase();
      if (normalized.indexOf("retry failed") !== -1) {
        this.retryFailed();
        return;
      }
      if (normalized.indexOf("pending void") !== -1) {
        this.confirmAndRetryVoids();
        return;
      }
      if (normalized.indexOf("sync") !== -1) {
        this.triggerSync("smart");
        return;
      }
      if (normalized.indexOf("status") !== -1) {
        this.setStatusFilter("failed");
        return;
      }
      this.fetchStatus();
    },
    async recreateSubscription(row) {
      if (!row || !row.subscription_id) {
        return;
      }

      this.rowRecreateLoadingId = row.subscription_id;

      try {
        const response = await recreateOffstreetSubscription({
          subscription_id: row.subscription_id,
          permit_id: this.permit.permit_id,
          async: this.syncAsync,
        });
        this.$toasted.show((response.data && response.data.message) || "Recreate request submitted.", {
          icon: "check-circle",
          className: "success-class",
        });
        this.fetchStatus();
      } catch (err) {
        this.showError(err);
      } finally {
        this.rowRecreateLoadingId = "";
      }
    },
    confirmAndRecreate(row) {
      this.$swal({
        title: "Recreate subscription",
        text: "Are you sure you want to force recreate for this subscription?",
        showCancelButton: true,
      }).then((result) => {
        if (result.value) {
          this.recreateSubscription(row);
        }
      });
    },
    async retryFailed() {
      if (!this.permit.permit_id) {
        return;
      }

      this.retryButtons.failed = true;

      try {
        const response = await retryFailedOffstreetRecreates({
          permit_id: this.permit.permit_id,
          async: this.syncAsync,
        });
        const data = this.extractApiData(response);
        this.hints = this.normalizeHints(data.operator_hints);
        this.failedDetails = Array.isArray(data.failed_details) ? data.failed_details : [];
        this.$toasted.show((response.data && response.data.message) || "Retry failed recreates queued.", {
          icon: "check-circle",
          className: "success-class",
        });
        this.fetchStatus();
      } catch (err) {
        this.showError(err);
      } finally {
        this.retryButtons.failed = false;
      }
    },
    confirmAndRetryVoids() {
      this.$swal({
        title: "Retry pending voids",
        text: "Are you sure you want to retry pending void records?",
        showCancelButton: true,
      }).then((result) => {
        if (result.value) {
          this.retryPendingVoids();
        }
      });
    },
    async retryPendingVoids() {
      if (!this.permit.permit_id) {
        return;
      }

      this.retryButtons.voids = true;

      try {
        const response = await retryPendingOffstreetVoids({
          permit_id: this.permit.permit_id,
          async: this.syncAsync,
        });
        const data = this.extractApiData(response);
        this.hints = this.normalizeHints(data.operator_hints);
        this.failedDetails = Array.isArray(data.failed_details) ? data.failed_details : [];
        this.$toasted.show((response.data && response.data.message) || "Retry pending voids queued.", {
          icon: "check-circle",
          className: "success-class",
        });
        this.fetchStatus();
      } catch (err) {
        this.showError(err);
      } finally {
        this.retryButtons.voids = false;
      }
    },
  },
};
</script>

<style scoped>
.offstreet-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.offstreet-panel {
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.02);
}

.offstreet-context {
  border: 1px dashed rgba(255, 255, 255, 0.16);
  border-radius: 8px;
  padding: 10px;
}

.offstreet-hints {
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 10px;
}

.offstreet-hint-pill {
  font-size: 11px;
  color: #fff;
  background: rgba(24, 154, 255, 0.2);
  border: 1px solid rgba(24, 154, 255, 0.45);
  border-radius: 16px;
  padding: 4px 10px;
  margin-right: 8px;
  margin-bottom: 8px;
  cursor: pointer;
}

.offstreet-filter-chip {
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #fff;
  background: transparent;
  border-radius: 16px;
  padding: 4px 10px;
  font-size: 12px;
  margin-left: 6px;
  text-transform: capitalize;
}

.offstreet-filter-chip.active {
  border-color: #2cbf7f;
  color: #2cbf7f;
}

.offstreet-summary-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.offstreet-summary-pill {
  font-size: 11px;
  padding: 4px 10px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #fff;
  background: rgba(255, 255, 255, 0.04);
}

.offstreet-summary-pill.summary-good {
  color: #2cbf7f;
  border-color: rgba(44, 191, 127, 0.4);
}

.offstreet-summary-pill.summary-danger {
  color: #ff6b6b;
  border-color: rgba(255, 107, 107, 0.45);
}

.offstreet-summary-pill.summary-subdued {
  color: #9aa2a9;
  border-color: rgba(154, 162, 169, 0.4);
}

.offstreet-summary-pill.summary-warn {
  color: #f5d97d;
  border-color: rgba(245, 217, 125, 0.45);
}

.offstreet-summary-pill.summary-neutral {
  color: #73c2fb;
  border-color: rgba(115, 194, 251, 0.45);
}

.offstreet-status-chip {
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  text-transform: lowercase;
}

.offstreet-cutover-chip {
  text-transform: none;
}

.status-success {
  color: #2cbf7f;
  border-color: rgba(44, 191, 127, 0.4);
}

.status-danger {
  color: #ff6b6b;
  border-color: rgba(255, 107, 107, 0.45);
}

.status-neutral {
  color: #f5d97d;
  border-color: rgba(245, 217, 125, 0.45);
}

.status-subdued {
  color: #9aa2a9;
  border-color: rgba(154, 162, 169, 0.4);
}

.offstreet-inline-action {
  background: transparent;
  border: none;
  font-size: 12px;
  cursor: pointer;
}

.offstreet-inline-action:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.offstreet-source-chip {
  display: inline-block;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  padding: 2px 8px;
  text-transform: capitalize;
}

.offstreet-source-chip.source-permit {
  color: #2cbf7f;
  border-color: rgba(44, 191, 127, 0.45);
}

.offstreet-source-chip.source-property {
  color: #f5d97d;
  border-color: rgba(245, 217, 125, 0.45);
}

.offstreet-source-chip.source-none {
  color: #9aa2a9;
  border-color: rgba(154, 162, 169, 0.4);
}

.offstreet-error-cell {
  max-width: 220px;
  word-break: break-word;
}

@media (max-width: 991px) {
  .offstreet-grid {
    grid-template-columns: 1fr;
  }
}
</style>
